/**
 * 상담 문의 공용 조각 — v2 목업 06(상담 문의) · S6(상담 접수 완료), 2026-09-22 v2h.
 *
 * 사용처: SideInquiryPage(/side-inquiry/:id) · GeneralInquiryPage(/side-inquiry) · 상세 InquirySection.
 * 정책:
 *   - 외부 링크 없음(카카오톡 채널만 허용). 약관 전문은 내부 /privacy.
 *   - 전화는 resolveContactPhone 결과(담당자 현장 번호 / 그 외 1533-8124).
 *     담당자 없는 현장(is_side_inquiry)은 전화 버튼을 숨긴다(상세 하단 CTA 규칙과 동일).
 *   - 제출 API·필드·검증은 각 페이지 기존 로직 그대로. 여기서는 표시만 한다.
 *   - 백엔드가 받지 않는 항목(관심 타입·상담 희망 시간·문의 내용·마케팅 동의)은 만들지 않는다.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../design-system/components';
import { INQUIRY_KAKAO_CHANNEL_URL, handleKakaoChatClick } from '../../domain/inquiry/PHONE';
import type { ContactPhone } from '../../domain/inquiry/resolveContactPhone';
import { formatPriceForDisplay } from '../../domain/property/formatters';
import { formatMoveInDate } from '../../utils/formatters';

const PhoneIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ChatIcon: React.FC = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

/** 목업 `.field label` / `.input` 공통 클래스 */
export const fieldLabelClass = 'mb-1.5 block text-[12.5px] font-bold text-body';
export const inputClass =
  'h-[46px] w-full rounded-ca-xl border border-ca-line bg-frame px-3.5 text-[14.5px] text-ink outline-none transition-colors placeholder:text-mute2 focus:border-accent disabled:opacity-60 box-border';

/**
 * 개인정보·마케팅 동의 — 이탈을 줄이려 작고 옅게 표기(대표 2026-09-22 "최대한 간단하게 작은 글씨로").
 * [필수] 개인정보: 체크해야 제출(클라이언트 확인만, 서버 전송 없음).
 * 체크박스는 [필수] 개인정보 1개만 노출(대표 2026-09-22 "체크박스 1개로만"). 마케팅 동의는 필수와 합치면 법적 효력이 없어 빼 둠 — MarketingConsentLine 은 미사용 보관.
 */
const consentBoxClass = 'mt-[1px] h-[13px] w-[13px] shrink-0 accent-[var(--ca-accent)]';
const consentTextClass = 'flex cursor-pointer items-start gap-1.5 text-[11px] leading-4 text-mute2';

export const MarketingConsentLine: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <label className={consentTextClass}>
    <input type="checkbox" name="marketing_consent" disabled={disabled} className={consentBoxClass} data-testid="marketing-consent" />
    <span>[선택] 분양·혜택 소식 수신 동의</span>
  </label>
);

/** 상세 문의 폼용 — 상태 없이 native required 로 [필수] 확인 */
export const ConsentLines: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <div className="flex flex-col gap-1">
    <label className={consentTextClass}>
      <input type="checkbox" name="privacy_consent" required disabled={disabled} className={consentBoxClass} data-testid="privacy-consent" />
      <span>
        [필수] 개인정보 수집·이용 동의{' '}
        <Link to="/privacy" className="underline">보기</Link>
      </span>
    </label>
  </div>
);

/** [필수] 개인정보 수집·이용 동의 — 체크해야 제출 가능(06 문의 페이지, 상태 제어형) */
export const PrivacyConsent: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <div className="flex flex-col gap-1">
    <label className={consentTextClass}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className={consentBoxClass}
        data-testid="privacy-consent"
      />
      <span>
        [필수] 개인정보 수집·이용 동의{' '}
        <Link to="/privacy" className="underline">보기</Link>
      </span>
    </label>
  </div>
);

export interface InquiryPropertySummary {
  title: string;
  address?: string | null;
  image?: string | null;
  bannerText?: string | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  priceLabel?: string;
  moveInDate?: string | null;
}

const shortRegion = (address?: string | null) => {
  if (!address) return '';
  const parts = address.split(/\s+/).filter(Boolean);
  return parts.slice(1, 3).join(' ');
};

/** 현장 요약 카드 (목업 06 상단 카드 / PC 우측 사이드) */
export const PropertySummaryCard: React.FC<{
  summary: InquiryPropertySummary;
  phone?: ContactPhone | null;
  showDetails?: boolean;
  showActions?: boolean;
}> = ({ summary, phone, showDetails = false, showActions = false }) => {
  const price = formatPriceForDisplay(summary.minPrice ?? undefined, summary.maxPrice ?? undefined);
  const moveIn = summary.moveInDate ? formatMoveInDate(summary.moveInDate) : '';
  return (
    <div className="flex flex-col gap-3 rounded-ca-3xl bg-frame p-4 shadow-ca-card">
      <div className="flex items-center gap-2.5">
        <div className="h-16 w-[76px] shrink-0 overflow-hidden rounded-ca-lg bg-block">
          {summary.image && <img src={summary.image} alt="" loading="lazy" className="h-full w-full object-cover" />}
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          {summary.bannerText && (
            <span className="self-start">
              <Badge tone="o" size="tag">{summary.bannerText}</Badge>
            </span>
          )}
          <b className="truncate text-[15px] font-bold text-ink">{summary.title}</b>
          {shortRegion(summary.address) && <span className="truncate text-[12.5px] text-muted">{shortRegion(summary.address)}</span>}
        </div>
      </div>

      {showDetails && (
        <div className="flex flex-col gap-1.5 border-t border-ca-line pt-3 text-[12.5px]">
          {price !== '가격 문의' && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted">{summary.priceLabel || '분양가'}</span>
              <b className="num text-[13.5px] font-bold text-accent">{price}</b>
            </div>
          )}
          {moveIn && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted">입주</span>
              <b className="num text-[13.5px] font-bold text-ink">{moveIn}</b>
            </div>
          )}
          {phone && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted">{phone.isDefault ? '상담 전화' : '현장 직통'}</span>
              <b className="num text-[13.5px] font-bold text-ink">{phone.display}</b>
            </div>
          )}
        </div>
      )}

      {showActions && <CallKakaoButtons phone={phone} stacked />}
    </div>
  );
};

/** 바로 전화 + 카카오 상담 (목업 06 하단 / PC 사이드).
 *  phone 이 없으면(담당자 없는 사이드 현장) **아무것도 그리지 않는다** — 2026-10-05 대표:
 *  "담당자 없는 현장은 1533-8124·카톡이 연결되면 안 된다. 폼(사이드 문의)만." */
export const CallKakaoButtons: React.FC<{ phone?: ContactPhone | null; stacked?: boolean }> = ({ phone, stacked = false }) =>
  !phone ? null : (
  <div className={`flex gap-2 ${stacked ? 'flex-col border-t border-ca-line pt-3 [&>a]:flex-none' : ''}`}>
    {phone && (
      <a
        href={phone.tel}
        aria-label={`바로 전화 ${phone.display}`}
        className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-ca-xl bg-ink text-[14px] font-bold text-white"
      >
        <PhoneIcon /> 바로 전화
      </a>
    )}
    {/* 카톡 유입경로 전달을 위해 noreferrer 금지(프로젝트 규칙) — rel="noopener" + referrerPolicy 유지 */}
    {/* eslint-disable-next-line react/jsx-no-target-blank */}
    <a
      href={INQUIRY_KAKAO_CHANNEL_URL}
      target="_blank"
      rel="noopener"
      referrerPolicy="unsafe-url"
      onClick={handleKakaoChatClick}
      className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-ca-xl bg-[#FEE500] text-[14px] font-bold text-[#191919]"
    >
      <ChatIcon /> 카카오 상담
    </a>
  </div>
);

/** PC 우측 '상담 안내' 카드 */
export const InquiryGuideCard: React.FC = () => (
  <div className="flex flex-col gap-2 rounded-ca-3xl bg-frame p-4 shadow-ca-card">
    <b className="text-[14px] font-bold text-ink">상담 안내</b>
    <span className="text-[12.5px] leading-5 text-muted">
      · 월~일 09:00 ~ 22:00 상담
      <br />· 남겨주신 연락처로 담당 상담원이 회신드립니다
    </span>
  </div>
);

/** S6 상담 접수 완료 (목업 상태 변형 06). 접수번호·희망 시간은 서버가 주지 않아 표시하지 않는다. */
export const InquiryDone: React.FC<{
  title?: string;
  message?: string;
  customerPhone?: string | null;
  propertyTitle?: string | null;
  phone?: ContactPhone | null;
  backTo?: string | null;
  backLabel?: string;
}> = ({
  title = '상담 신청이 접수되었습니다',
  message = '담당 상담원이 영업시간 내에 연락드립니다.',
  customerPhone,
  propertyTitle,
  phone,
  backTo,
  backLabel = '단지 상세로 돌아가기',
}) => (
  <div className="flex flex-col gap-2.5" data-testid="inquiry-done">
    <div className="flex flex-col items-center gap-2.5 px-6 pb-4 pt-10 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ECFDF5] text-ok">
        <svg width={32} height={32} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.2-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4-7 7z" />
        </svg>
      </div>
      <h2 className="text-[19px] font-bold text-ink">{title}</h2>
      <p className="text-[14px] leading-6 text-muted">
        {customerPhone ? (
          <>
            담당 상담원이 <b className="num text-ink">{customerPhone}</b>로
            <br />
            연락드립니다.
          </>
        ) : (
          message
        )}
      </p>
    </div>

    {(propertyTitle || phone) && (
      <div className="flex flex-col gap-2 rounded-ca-3xl bg-frame p-4 text-[13px] shadow-ca-card">
        {propertyTitle && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted">관심 단지</span>
            <b className="min-w-0 truncate text-right font-bold text-ink">{propertyTitle}</b>
          </div>
        )}
        {phone && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted">{phone.isDefault ? '상담 전화' : '현장 직통'}</span>
            <b className="num font-bold text-ink">{phone.display}</b>
          </div>
        )}
      </div>
    )}

    {phone && (
      <div className="flex flex-col gap-1.5 rounded-ca-3xl bg-accent-50 p-4">
        <b className="text-[14px] font-bold text-accent">지금 바로 통화도 가능합니다</b>
        <span className="text-[12.5px] text-body">잔여 동호수·계약 조건은 전화 상담이 가장 빠릅니다.</span>
        <a
          href={phone.tel}
          className="mt-1 flex h-11 items-center justify-center gap-1.5 rounded-ca-xl bg-accent text-[14px] font-bold text-white shadow-ca-cta"
        >
          <PhoneIcon /> {phone.isDefault ? '전화 상담' : '현장 직통'} <span className="num">{phone.display}</span>
        </a>
      </div>
    )}

    <div className="flex gap-2">
      {backTo && (
        <Link
          to={backTo}
          className="flex h-11 flex-1 items-center justify-center rounded-ca-xl bg-block text-[14px] font-bold text-ink"
        >
          {backLabel}
        </Link>
      )}
      <Link
        to="/"
        className="flex h-11 flex-1 items-center justify-center rounded-ca-xl border border-ca-line bg-frame text-[14px] font-bold text-body"
      >
        다른 현장 보기
      </Link>
    </div>
  </div>
);
